import { Navigate, Route } from 'react-router-dom';
import { OrgPageNotFound, OrgScope, ShortFormRedirect } from '@/components/layouts/OrgScope';
import { BarChart3, BookOpen, Files } from 'lucide-react';
import { ComingSoonPage } from '@/components/core/ComingSoonPage';
import { CompanySectionGate } from '@/features/company/components/CompanySectionGate';
import { lazyPage } from './lazy';

const CompanyDashboardPage = lazyPage(
  () => import('@/features/company/pages/DashboardPage'),
  'CompanyDashboardPage'
);
const HiringPipelinePage = lazyPage(
  () => import('@/features/company/pages/HiringPipelinePage'),
  'HiringPipelinePage'
);
const JobsPage = lazyPage(() => import('@/features/company/pages/JobsPage'), 'JobsPage');
const CreateJobPage = lazyPage(
  () => import('@/features/company/pages/CreateJobPage'),
  'CreateJobPage'
);
const CandidatesPage = lazyPage(
  () => import('@/features/company/pages/CandidatesPage'),
  'CandidatesPage'
);
const CandidateDetailPage = lazyPage(
  () => import('@/features/company/pages/CandidateDetailPage'),
  'CandidateDetailPage'
);
const InterviewsPage = lazyPage(
  () => import('@/features/company/pages/InterviewsPage'),
  'InterviewsPage'
);
const OrgMessagesPage = lazyPage(
  () => import('@/features/company/pages/OrgMessagesPage'),
  'OrgMessagesPage'
);
const OrgTeamPage = lazyPage(() => import('@/features/company/pages/OrgTeamPage'), 'OrgTeamPage');
const AnalyticsPage = lazyPage(
  () => import('@/features/company/pages/AnalyticsPage'),
  'AnalyticsPage'
);
const BrandingPage = lazyPage(
  () => import('@/features/company/pages/BrandingPage'),
  'BrandingPage'
);
const CompanySettingsPage = lazyPage(
  () => import('@/features/company/pages/CompanySettingsPage'),
  'CompanySettingsPage'
);
const CompanyBillingPage = lazyPage(
  () => import('@/features/company/pages/CompanyBillingPage'),
  'CompanyBillingPage'
);
const CompanyCheckoutSuccessPage = lazyPage(
  () => import('@/features/company/pages/CompanyCheckoutSuccessPage'),
  'CompanyCheckoutSuccessPage'
);

const CandidateMatchPage = lazyPage(
  () => import('@/features/company/pages/CandidateMatchPage'),
  'CandidateMatchPage'
);

export const CompanyRoutes = () => [
  // No organization named at all: resolve one (lib/org-path.ts).
  <Route key="company-root" path="/company" element={<ShortFormRedirect ctx="company" />} />,
  // /company/<slug>/… — the URL picks the company (OrgScope); pages a member's
  // role can't use explain themselves instead of failing (CompanySectionGate).
  <Route key="company-org" path="/company/:orgSlug" element={<OrgScope ctx="company" />}>
    <Route index element={<Navigate to="dashboard" replace />} />
    <Route element={<CompanySectionGate />}>
      <Route path="dashboard" element={<CompanyDashboardPage />} />

      {/* Jobs */}
      <Route path="jobs" element={<JobsPage />} />
      <Route path="jobs/create" element={<CreateJobPage />} />
      <Route path="jobs/:id/edit" element={<CreateJobPage />} />
      <Route path="jobs/:id/matches" element={<CandidateMatchPage />} />
      <Route
        path="jobs/drafts"
        element={
          <ComingSoonPage
            title="Draft Jobs"
            description="Save job postings as drafts and publish them when ready. Draft management is coming soon."
            icon={<Files size={24} />}
            expectedIn="Coming soon"
          />
        }
      />
      <Route
        path="jobs/archived"
        element={
          <ComingSoonPage
            title="Archived Jobs"
            description="View and restore previously archived job postings. Archiving helps keep your active list clean."
            icon={<Files size={24} />}
            expectedIn="Coming soon"
          />
        }
      />

      {/* Candidates */}
      <Route path="candidates" element={<CandidatesPage />} />
      <Route path="candidates/shortlisted" element={<CandidatesPage />} />
      <Route path="candidates/talent-pool" element={<CandidatesPage />} />
      <Route path="candidates/:applicationId" element={<CandidateDetailPage />} />

      {/* Assessments */}
      <Route
        path="assessments"
        element={
          <ComingSoonPage
            title="Assessment Library"
            description="Create and manage skill assessments for candidates. Build coding tests, MCQ banks, and custom evaluations."
            icon={<BookOpen size={24} />}
            expectedIn="V2"
          />
        }
      />
      <Route
        path="assessments/results"
        element={
          <ComingSoonPage
            title="Candidate Results"
            description="Review assessment scores and detailed performance breakdowns for each candidate."
            icon={<BarChart3 size={24} />}
            expectedIn="V2"
          />
        }
      />

      {/* Interviews */}
      <Route path="interviews" element={<InterviewsPage />} />
      <Route
        path="interviews/feedback"
        element={
          <ComingSoonPage
            title="Interview Feedback"
            description="Structured feedback forms for interviewers to evaluate candidates after each interview round."
            icon={<BookOpen size={24} />}
            expectedIn="V2"
          />
        }
      />

      {/* Messages */}
      <Route path="messages" element={<OrgMessagesPage />} />

      {/* Team & Organization */}
      <Route path="organization" element={<OrgTeamPage />} />
      <Route
        path="organization/roles"
        element={
          <ComingSoonPage
            title="Roles & Permissions"
            description="Create custom roles with fine-grained permissions for your hiring team members."
            icon={<BookOpen size={24} />}
            expectedIn="V2"
          />
        }
      />
      <Route
        path="organization/departments"
        element={
          <ComingSoonPage
            title="Departments"
            description="Organize your team into departments and assign department heads for structured hiring workflows."
            icon={<Files size={24} />}
            expectedIn="V2"
          />
        }
      />

      {/* Analytics, Branding, Documents, Pipeline, Settings */}
      <Route path="analytics" element={<AnalyticsPage />} />
      <Route path="branding" element={<BrandingPage />} />
      <Route
        path="documents"
        element={
          <ComingSoonPage
            title="Shared Documents"
            description="Store and share offer letters, NDAs, onboarding documents, and other hiring materials with your team."
            icon={<Files size={24} />}
            expectedIn="V2"
          />
        }
      />
      <Route path="pipeline" element={<HiringPipelinePage />} />
      <Route path="billing" element={<CompanyBillingPage />} />
      <Route path="checkout/success" element={<CompanyCheckoutSuccessPage />} />
      <Route path="settings" element={<CompanySettingsPage />} />
    </Route>
    <Route path="*" element={<OrgPageNotFound ctx="company" />} />
  </Route>,
];
